import { useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { Button } from '@newfront-insurance/core-ui/v2';
import { Loader2 } from 'lucide-react';

import { ALL_CUSTOM_SKILL_FILTERS } from './custom-skills-settings/custom-skill-model';
import { CreateSkillView } from './custom-skills-ui';
import { useSettingsPanelContext } from './settings-panel-context';
import { GET_CUSTOM_SKILLS, GET_SHARED_CUSTOM_SKILLS } from '../../../queries/chats';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';

export function CustomSkillEditScreen({ skillUuid }: { skillUuid: string }) {
  const { userUuid } = useChatOverlayContext();
  const { popView, backHandlerRef } = useSettingsPanelContext();
  const skillsEnabled = useFeatureFlag('nova-skills', false);
  const managementEnabled = useFeatureFlag('nova-skills-management', false);
  const enabled = skillsEnabled && managementEnabled && !!userUuid;
  // These fields/arguments match the collections query, so Apollo reuses the list's cache.
  const own = useQuery(GET_CUSTOM_SKILLS, {
    variables: { filters: ALL_CUSTOM_SKILL_FILTERS },
    skip: !enabled,
    fetchPolicy: 'cache-first',
  });
  const shared = useQuery(GET_SHARED_CUSTOM_SKILLS, {
    variables: { filters: { userUuid, enabled: [true, false] } },
    skip: !enabled,
    fetchPolicy: 'cache-first',
  });
  const skill = [...(own.data?.customSkills ?? []), ...(shared.data?.sharedCustomSkills ?? [])].find(
    (row) => row.uuid === skillUuid,
  );
  if (!enabled) return null;
  if (!skill && (own.loading || shared.loading))
    return (
      <div role="status" data-testid="nova-skill-edit-loading" className="flex items-center justify-center px-5 py-8">
        <Loader2 aria-hidden="true" className="text-muted-foreground h-5 w-5 animate-spin" />
        <span className="text-muted-foreground ml-2 text-sm">Loading skill...</span>
      </div>
    );
  if (!skill)
    return (
      <div
        data-testid="nova-skill-edit-unavailable"
        className="border-border bg-background mx-5 my-5 rounded-md border p-4"
      >
        <p className="text-muted-foreground text-sm">
          {own.error || shared.error ? 'Unable to load this skill.' : 'This skill is no longer available.'}
        </p>
        <Button type="button" data-testid="nova-skill-edit-back-button" className="mt-3" onClick={popView}>
          Back to skills
        </Button>
      </div>
    );
  return <CreateSkillView key={skill.uuid} onBack={popView} skillToUpdate={skill} cancelRef={backHandlerRef} />;
}
